import {
  useEffect, useState, useContext, ComponentType, ReactNode,
} from 'react';
import useSetState from '#utils/use-set-state';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import YetAnotherLightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import type { TFunction } from 'i18next';
import 'yet-another-react-lightbox/styles.css';
import './Product.css';

import {
  saveDraft,
} from '#features/cart/draft.slice';
import {
  useGetProductWithInventoryMutation,
} from '#services/product';
import {
  useGetCartMutation,
  useClearCartMutation,
  useAddToCartMutation,
} from '#services/cart';
import { FeedbackUIContext } from '#context/feedback-ui-context';
import useForm from '#utils/use-form';
import {
  CART_ITEM_TYPES,
} from '#common/constants';
import {
  formatDate,
  formatCurrency,
  calcCartItemWholesalePrice,
  calcCartItemUnitPrice,
  anchorDayToTZ,
  startOfDayInTZ,
  getWearDateBufferDays,
} from '#common/utils';
import { orderValidations, variantSelectionValidations } from '#common/validation';
import { getWearDateError } from '#common/validation/order';
import { dateLocaleFor, translateWearDateError } from '#utils/wear-date-error';
import InventoryProduct from './InventoryProduct';

interface AppConfig {
  shippingDelayInWeeks: number;
  shippingWindowInWeeks?: number | string;
  sizeChartImage?: string;
  timeZone?: string;
  wholesaleEmail?: string;
  wearDateBufferDays?: number | string;
  [key: string]: unknown;
}

interface ShippingWindow {
  name?: string;
  weeks?: number | string;
  price?: number | string;
  [key: string]: unknown;
}

interface Customization {
  name?: string;
  price?: number | string;
  [key: string]: unknown;
}

interface ProductVariant {
  variant?: string;
  inventoryOnly?: boolean;
  [key: string]: unknown;
}

interface ProductVideoRaw {
  url?: string;
  [key: string]: unknown;
}

interface ProductCollectionRef {
  name?: string;
  [key: string]: unknown;
}

interface ProductInventoryItem {
  color?: string;
  size?: string;
  amounts: number;
  availabilityStatus?: string;
  availableDate?: string | Date | null;
  [key: string]: unknown;
}

interface ProductData {
  id: string | number;
  version?: number;
  name?: string;
  sku?: string;
  price?: number;
  description?: string;
  isNew?: boolean;
  showVideoFirst?: boolean;
  media?: string[];
  videos?: ProductVideoRaw[];
  colors: string[];
  sizes: string[];
  variants?: ProductVariant[];
  customizations?: Customization[];
  collections?: ProductCollectionRef[];
  shippingWindow?: ShippingWindow[];
  inventoryProduct?: ProductInventoryItem[];
  [key: string]: unknown;
}

interface ProductFormValues {
  color?: string;
  size?: string;
  wearDate?: string | Date;
  poNumber?: string;
  selectedCustomizations: Record<string, boolean>;
  customizationNotes: Record<string, string>;
  selectedShippingWindow: number;
  amounts: number;
  fromInventory?: boolean;
  inStock?: boolean;
  availableDate?: string | Date | null;
  maxAmounts?: number;
  shippingWindowInWeeks?: number;
  shippingDelayInWeeks?: number;
  variants?: ProductVariant[];
  [key: string]: unknown;
}

interface MediaImage {
  type: 'image';
  url: string;
}

interface MediaVideo {
  type: 'video';
  thumbnailUrl: string;
  url: string;
}

interface MediaPlaceholder {
  type: 'placeholder';
}

type MediaEntry = MediaImage | MediaVideo;

const IMAGE_PLACEHOLDER = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='680.764' height='528.354' viewBox='0 0 180.119 139.794'%3E%3Cg transform='translate(-13.59 -66.639)' paint-order='fill markers stroke'%3E%3Cpath fill='%23d0d0d0' d='M13.591 66.639H193.71v139.794H13.591z'/%3E%3Cpath d='m118.507 133.514-34.249 34.249-15.968-15.968-41.938 41.937H178.726z' opacity='.675' fill='%23fff'/%3E%3Ccircle cx='58.217' cy='108.555' r='11.773' opacity='.675' fill='%23fff'/%3E%3Cpath fill='none' d='M26.111 77.634h152.614v116.099H26.111z'/%3E%3C/g%3E%3C/svg%3E";

const Lightbox = YetAnotherLightbox as unknown as ComponentType<Record<string, unknown>>;

function groupInventoryItems(
  inventory?: ProductInventoryItem[],
  timeZone?: string,
): ProductInventoryItem[] | undefined {
  if (!inventory) {
    return undefined;
  }

  const groupedMap = inventory.reduce<Record<string, ProductInventoryItem>>((acc, item) => {
    // For inProduction items, include the date in the key to keep them separate
    // For inStock items, don't include date so they get grouped together
    let key = `${item.color}-${item.size}-${item.availabilityStatus}`;

    if (item.availabilityStatus === 'inProduction' && item.availableDate) {
      // Key by the MARKET-timezone day so the grouping matches the server-side
      // stock range (startOfDayInTZ/endOfDayInTZ in add-to-cart). Using the
      // browser-local day here would let a shopper see a combined quantity the
      // server then splits across two market days and rejects.
      const dateKey = String(startOfDayInTZ(timeZone, item.availableDate).getTime());
      key += `-${dateKey}`;
    }

    if (!acc[key]) {
      acc[key] = {
        ...item,
        amounts: 0,
      };
    }

    const entry = acc[key]!;
    entry.amounts += item.amounts;

    // For inStock items, keep the earliest availableDate
    if (item.availabilityStatus === 'inStock' && item.availableDate) {
      if (!entry.availableDate
        || new Date(item.availableDate) < new Date(entry.availableDate)) {
        entry.availableDate = item.availableDate;
      }
    }

    return acc;
  }, {});

  return Object.values(groupedMap);
}

function getShippingWindows(
  product: ProductData | undefined,
  appConfig: AppConfig,
  t: TFunction,
): ShippingWindow[] {
  if (product && Array.isArray(product.shippingWindow) && product.shippingWindow.length) {
    return product.shippingWindow;
  }

  return [{
    name: t('shippingWindow.standard'),
    weeks: Number(appConfig.shippingWindowInWeeks) || 17,
    price: 0,
  }];
}

// Compute an estimated ship window based on weeks-out from today. Returns a
// nice "Apr 20 – May 01, 2026" style string for the rail header, localised to
// the active UI language so month names/order follow the storefront locale.
function formatShipWindowRange(
  weeks: number | undefined,
  delayWeeks: number | undefined,
  t: TFunction,
  locale = 'en-US',
): string {
  if (!Number.isFinite(weeks) || (weeks as number) <= 0) {
    return t('shipsWithin5Days');
  }

  const totalWeeks = Number(weeks) + (Number(delayWeeks) || 0);
  const start = new Date();
  start.setDate(start.getDate() + (totalWeeks * 7) - 5);
  const end = new Date();
  end.setDate(end.getDate() + (totalWeeks * 7) + 5);

  const startStr = new Intl.DateTimeFormat(locale, { month: 'short', day: '2-digit' }).format(start);
  const endStr = new Intl.DateTimeFormat(locale, { month: 'short', day: '2-digit', year: 'numeric' }).format(end);
  return `${startStr} – ${endStr}`;
}

export default function Product() {
  const { t, i18n } = useTranslation('shop');
  const appConfig = useSelector((state: { appConfig: AppConfig }) => state.appConfig);
  const userInfo = useSelector(
    (state: { auth: { userInfo?: { isCustomer?: boolean } } }) => state.auth.userInfo,
  );
  const [product, setProduct] = useState<ProductData>();
  const [readonly, setReadonly] = useState(false);
  const [getProductWithInventory] = useGetProductWithInventoryMutation();
  const [addToCart] = useAddToCartMutation();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();

  const [uiState, setUIState] = useSetState<{
    fullscreenImageOpen: boolean;
    mediaPreviewIndex: number;
  }>({
    fullscreenImageOpen: false,
    mediaPreviewIndex: 0,
  });
  const setMediaPreviewIndex = (mediaPreviewIndex: number) => setUIState({ mediaPreviewIndex });
  const setFullscreenImageOpen = (fullscreenImageOpen: boolean) => setUIState({ fullscreenImageOpen });

  const [getCart] = useGetCartMutation();
  const [clearCart] = useClearCartMutation();

  // current item will be added as inventory order
  const isInventory = location.search.includes('inventory=true');
  // this item will be add to customer order when click the "add to cart" item
  const isAddToOrder = (!userInfo?.isCustomer && searchParams.has('orderId'));
  const addToOrderUserId = (!userInfo?.isCustomer && Number(searchParams.get('userId')));

  const validations = isInventory ? variantSelectionValidations : orderValidations;
  const {
    values,
    setValues,
    isValid,
    errors,
    errorMessages,
    setTouched,
    handleChange,
    handleSubmit,
    addSubmitListener,
  } = useForm({
    initialState: {
      color: '',
      size: '',
      wearDate: new Date(),
      poNumber: '',
      selectedCustomizations: {},
      customizationNotes: {},
      selectedShippingWindow: 0,
      amounts: 1,
      // current item to be order is from inventory
      fromInventory: false,
      shippingWindowInWeeks: 0,
      shippingDelayInWeeks: appConfig.shippingDelayInWeeks,
      // market timezone for wear-date day-boundary math (calcMinWearDate)
      timeZone: appConfig.timeZone,
      // admin-configured margin between the estimated ship date and wear date
      wearDateBufferDays: getWearDateBufferDays(appConfig),
    },
    validations,
  });

  const fv = values as ProductFormValues;

  const {
    showBackdrop,
    hideBackdrop,
    simpleSnackbar,
    showModal,
    setModalData,
    showDialog,
    hideDialog,
    setDialogData,
  } = useContext(FeedbackUIContext)!;

  useEffect(() => {
    const meta = document.querySelector('meta[name="viewport"]');
    if (meta) {
      meta.setAttribute('content', 'width=device-width, initial-scale=1.0');
    } else {
      const newMeta = document.createElement('meta');
      newMeta.name = 'viewport';
      newMeta.content = 'width=device-width, initial-scale=1.0';
      document.head.appendChild(newMeta);
    }

    return () => {
      const viewportMeta = document.querySelector('meta[name="viewport"]');
      if (viewportMeta) {
        viewportMeta.remove();
      }
    };
  }, []);

  useEffect(() => {
    const match = location.search.match(/productId=(\S+)/i);
    if (!match) {
      return;
    }

    showBackdrop();
    void getProductWithInventory({ productId: match[1], addToOrderUserId })
      .then((res) => {
        hideBackdrop();

        const hasError = simpleSnackbar(res as unknown as Parameters<typeof simpleSnackbar>[0]);
        if (hasError) {
          void navigate('/shop');
          return;
        }

        const resData = (res as { data: { product: ProductData; readonly?: boolean } }).data;
        const { product: productWithInventory } = resData;
        setProduct(productWithInventory);
        setReadonly(!!resData.readonly);
        setValues({
          variants: productWithInventory.variants,
        });
        // No default color / size: the customer must pick both. Marking them
        // touched up front makes the required errors count immediately, so the
        // Add to Cart button stays disabled until the variant is complete.
        handleChange(null, [{
          name: 'color',
          value: '',
        }, {
          name: 'size',
          value: '',
        }, {
          name: 'variants',
          value: productWithInventory.variants,
        }]);
      });
  }, []);

  useEffect(() => {
    void getCart(undefined).then((res) => {
      const result = res as { error?: unknown; data?: { items?: Array<{ type?: number }> } };
      if (result.error) {
        return;
      }
      let shouldClear = false;
      if (result.data?.items) {
        shouldClear = result.data.items.some((itm) => (
          isInventory
            ? itm.type !== CART_ITEM_TYPES.ADD_INVENTORY
            : itm.type === CART_ITEM_TYPES.ADD_INVENTORY
        ));
      }

      if (!shouldClear) {
        return;
      }

      setDialogData({
        title: t('clearCart.title'),
        text: isInventory ? t('clearCart.textInventory') : t('clearCart.textCustomer'),
        disagree: t('clearCart.back'),
        agree: t('clearCart.confirm'),
        onDisagree: () => {
          hideDialog();
          void navigate(-1);
        },
        onAgree: () => {
          hideDialog();
          void clearCart({ isInventory });
        },
      });
      showDialog();
    });
  }, []);

  const shippingWindows = getShippingWindows(product, appConfig, t);

  const updateShippingWindowInWeeks = (inStock?: boolean) => {
    const currentShippingWindow = shippingWindows[fv.selectedShippingWindow]
      || shippingWindows[0];
    const shippingWindowInWeeks = inStock
      ? 0
      : Number(currentShippingWindow!.weeks);
    setValues({ shippingWindowInWeeks });
  };

  useEffect(() => {
    if (!product) {
      return;
    }
    updateShippingWindowInWeeks();
  }, [product]);

  if (userInfo?.isCustomer && isInventory) {
    return null;
  }

  if (!product) {
    return null;
  }

  // ----- compose media list -----------------------------------------------
  const imageList: MediaImage[] = [];
  (product.media || []).forEach((item) => {
    const url = `/images/product_${product.id.toString().padStart(8, '0')}/${item}`;
    imageList.push({
      type: 'image',
      url,
    });
  });

  const videoList: MediaVideo[] = [];
  (product.videos || []).forEach((item) => {
    if (!item.url) {
      return;
    }
    const match = item.url.match(/\/\/youtu\.be(\/)([a-zA-Z0-9-_]*)/)
      || item.url.match(/(?:watch|shorts|live)(\?v=|\/)?([a-zA-Z0-9-_]*)/);
    if (!match) {
      return;
    }
    const videoId = match[2];
    const videoThumbnail = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
    const videoUrl = `https://www.youtube.com/embed/${videoId}`;
    videoList.push({
      type: 'video',
      thumbnailUrl: videoThumbnail,
      url: videoUrl,
    });
  });

  const mediaList: MediaEntry[] = product.showVideoFirst
    ? [...videoList, ...imageList]
    : [...imageList, ...videoList];

  const mediaPreview: MediaEntry | MediaPlaceholder = mediaList.length
    ? mediaList[uiState.mediaPreviewIndex]!
    : { type: 'placeholder' };
  // ------------------------------------------------------------------------

  const wholesalePrice = calcCartItemWholesalePrice(
    values as unknown as Parameters<typeof calcCartItemWholesalePrice>[0],
    product as unknown as Parameters<typeof calcCartItemWholesalePrice>[1],
  );
  const unitPrice = calcCartItemUnitPrice(
    values as unknown as Parameters<typeof calcCartItemUnitPrice>[0],
    product as unknown as Parameters<typeof calcCartItemUnitPrice>[1],
  );
  const variantUpcharge = wholesalePrice - Number(product.price || 0);
  const customizationsList = (product.customizations || []).filter((c) => c.name);
  const hasCustomizations = customizationsList.length > 0;
  const totalPrice = unitPrice * fv.amounts;

  // selected count + total upcharge for the customization rail label
  const selectedCustCount = Object.keys(fv.selectedCustomizations || {})
    .filter((k) => fv.selectedCustomizations[k]).length;
  const selectedCustUpcharge = Object.keys(fv.selectedCustomizations || {})
    .filter((k) => fv.selectedCustomizations[k])
    .reduce((sum, k) => sum + Number(((product.customizations || [])[Number(k)] || {}).price || 0), 0);

  const wearDateBufferDays = getWearDateBufferDays(appConfig);
  const wearDateError = errors.wearDate ? getWearDateError(values) : null;

  const estimatedShipText = fv.fromInventory && fv.inStock
    ? t('shipsWithin5Days')
    : formatShipWindowRange(
      fv.shippingWindowInWeeks,
      appConfig.shippingDelayInWeeks,
      t,
      dateLocaleFor(i18n.language),
    );

  const handleAddToCart = (vals: Record<string, unknown>, valid: boolean) => {
    if (!valid) {
      return;
    }

    const v = vals as ProductFormValues;

    // Keep customization notes indexed by product option; unselected options
    // are dropped so the wire format only carries notes the customer can see.
    const newCustomizationNotes: Record<string, string> = {};
    Object.keys(v.selectedCustomizations).forEach((idx) => {
      const selected = v.selectedCustomizations[idx];
      if (!selected) {
        return;
      }
      newCustomizationNotes[idx] = v.customizationNotes[idx] || '';
    });
    const selectedCustomizationKeys = Object.keys(v.selectedCustomizations)
      .filter((idx) => v.selectedCustomizations[idx]);

    const postData: Record<string, unknown> = {
      ...vals,
      productId: product.id,
      productVersion: product.version,
      wholesalePrice,
      unitPrice,
      totalPrice,
      wearDate: v.wearDate,
      customizationNotes: newCustomizationNotes,
    };
    delete postData.shippingDelayInWeeks;
    delete postData.variants;
    // config-derived, not part of the cart item; the server re-resolves them
    delete postData.timeZone;
    delete postData.wearDateBufferDays;

    if (isInventory) {
      delete postData.poNumber;
      delete postData.selectedCustomizations;
      delete postData.wearDate;
      postData.type = CART_ITEM_TYPES.ADD_INVENTORY;
    } else if (v.fromInventory) {
      delete postData.maxAmounts;
      // export-orders/build-bulk-order-csv index these directly, so keep
      // empty objects rather than deleting the keys.
      postData.selectedCustomizations = {};
      postData.customizationNotes = {};
      postData.type = CART_ITEM_TYPES.IN_STOCK;
    } else if (selectedCustomizationKeys.length) {
      delete postData.maxAmounts;
      postData.type = CART_ITEM_TYPES.IN_PRODUCTION_CUSTOM;
    } else {
      delete postData.maxAmounts;
      postData.type = CART_ITEM_TYPES.IN_PRODUCTION;
    }

    if (isAddToOrder) {
      const orderId = searchParams.get('orderId');
      postData.product = product;
      postData.collectionNames = (product.collections || []).map((c) => c.name);

      dispatch(saveDraft({
        id: Number(orderId),
        data: postData,
      }));

      searchParams.delete('productId');
      searchParams.delete('userId');
      void navigate(`/management/order/detail?${searchParams.toString()}`);
      return;
    }

    showBackdrop();
    void addToCart(postData)
      .then((res) => {
        const hasError = simpleSnackbar(res as unknown as Parameters<typeof simpleSnackbar>[0]);
        if (hasError) {
          return;
        }
        const url = isInventory ? '/cart?inventory=true' : '/cart';
        void navigate(url);
      }).finally(() => {
        hideBackdrop();
      });
  };
  addSubmitListener(handleAddToCart);

  const showSizeChartModal = () => {
    const imgUrl = appConfig.sizeChartImage
      ? `/public/static/images/${appConfig.sizeChartImage}`
      : '';
    setModalData({
      children: (
        <Box>
          <img
            style={{ width: '100%' }}
            src={imgUrl}
            alt={t('sizeChartAlt')}
          />
        </Box>
      ),
    });
    showModal();
  };

  // ----- LEFT side: media -------------------------------------------------
  const renderMediaPreview = () => {
    if (mediaPreview.type === 'placeholder') {
      return (
        <img
          src={IMAGE_PLACEHOLDER}
          loading="lazy"
          alt={t('media.productDetailAlt')}
        />
      );
    }
    if (mediaPreview.type === 'image') {
      return (
        <button
          type="button"
          className="pd-main-img"
          onClick={() => setFullscreenImageOpen(true)}
          title={t('media.zoom')}
        >
          <img src={mediaPreview.url} loading="lazy" alt={t('media.productDetailAlt')} />
        </button>
      );
    }
    if (mediaPreview.type === 'video') {
      return (
        <iframe
          title={t('media.videoPreviewTitle')}
          src={mediaPreview.url}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />
      );
    }
    return null;
  };

  const renderMediaThumbnails = () => {
    if (!mediaList.length) {
      return null;
    }
    return (
      <div className="pd-thumb-row">
        {mediaList.map((item, idx) => {
          const isActive = uiState.mediaPreviewIndex === idx;
          if (item.type === 'image') {
            return (
              <button
                type="button"
                key={idx}
                className={`pd-thumb${isActive ? ' active' : ''}`}
                onClick={() => setMediaPreviewIndex(idx)}
              >
                <img src={item.url} alt="" loading="lazy" />
              </button>
            );
          }
          return (
            <button
              type="button"
              key={idx}
              className={`pd-thumb${isActive ? ' active' : ''}`}
              onClick={() => setMediaPreviewIndex(idx)}
            >
              <img src={item.thumbnailUrl} alt="" loading="lazy" />
              <span className="video-badge">
                <PlayCircleIcon style={{ fontSize: 26 }} />
              </span>
            </button>
          );
        })}
      </div>
    );
  };

  const renderFullScreenPreview = () => {
    if (!uiState.fullscreenImageOpen) {
      return null;
    }
    const slides = mediaList.map((item) => (
      item.type === 'video'
        ? { type: 'video-iframe', src: item.url }
        : { src: item.url }
    ));
    return (
      <Lightbox
        open={uiState.fullscreenImageOpen}
        close={() => setFullscreenImageOpen(false)}
        index={uiState.mediaPreviewIndex}
        slides={slides}
        plugins={[Zoom]}
        zoom={{ scrollToZoom: true }}
        on={{ view: ({ index }: { index: number }) => setMediaPreviewIndex(index) }}
        render={{
          slide: ({ slide }: { slide: { type?: string; src?: string } }) => {
            if (slide.type === 'video-iframe') {
              return (
                <iframe
                  title={t('media.videoTitle')}
                  width="995"
                  height="560"
                  src={slide.src}
                  frameBorder={0}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              );
            }
            return undefined;
          },
        }}
      />
    );
  };

  // ----- RIGHT side: purchase rail ----------------------------------------
  const renderColorChips = () => (
    <div className="pd-section">
      <div className="rail-label">
        <span className="t">
          {t('color')}
          <span className="req">*</span>
        </span>
        <span className="v">{fv.color || ''}</span>
      </div>
      <div className="color-chips">
        {product.colors.map((c, idx) => {
          const sel = fv.color === c;
          return (
            <button
              type="button"
              key={`${c}-${idx}`}
              className={`color-chip${sel ? ' sel' : ''}`}
              disabled={fv.fromInventory}
              onClick={() => handleChange(null, { name: 'color', value: c })}
            >
              <span className="sw" />
              {c}
            </button>
          );
        })}
      </div>
    </div>
  );

  const renderSizeChips = () => {
    // determine sold-out sizes (variants flagged inventoryOnly for the
    // currently selected color). Customers can't pick those directly — they
    // must add from the production schedule below.
    const inventoryOnlyVariants = (product.variants || []).filter((v) => v.inventoryOnly);
    const isSold = (s: string) => inventoryOnlyVariants.some((v) => v.variant === `${s} / ${fv.color}`);

    return (
      <div className="pd-section">
        <div className="rail-label">
          <span className="t">
            {t('size')}
            <span className="req">*</span>
          </span>
          {appConfig.sizeChartImage ? (
            <button type="button" className="link-btn" onClick={showSizeChartModal}>
              {t('sizeChart')}
            </button>
          ) : null}
        </div>
        <div className="size-chips">
          {product.sizes.map((s, idx) => {
            const sel = fv.size === s;
            const sold = isSold(s);
            return (
              <button
                type="button"
                key={`${s}-${idx}`}
                className={`size-chip${sel ? ' sel' : ''}${sold ? ' sold' : ''}`}
                disabled={fv.fromInventory || sold}
                onClick={() => handleChange(null, { name: 'size', value: s })}
                title={sold ? t('sizeSoldTooltip') : ''}
              >
                {s}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const renderDateAndPo = () => {
    if (isInventory) {
      return null;
    }
    return (
      <div className="field-grid">
        <div className={`mui-tf${errors.wearDate ? ' error' : ''}`}>
          <span className="lbl">
            {t('wearDate')}
            {' '}
            <span className="req">*</span>
          </span>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DesktopDatePicker
              format="MM/dd/yyyy"
              name="wearDate"
              disablePast
              value={new Date(fv.wearDate as string | Date)}
              onChange={(d) => {
                if (d && d.getDate()) {
                  // anchor the picked calendar day to the market timezone so a
                  // shopper east of the market isn't rejected on the minimum day
                  handleChange(null, {
                    name: 'wearDate',
                    value: anchorDayToTZ(appConfig.timeZone, d).toISOString(),
                  });
                }
              }}
              slotProps={{
                textField: {
                  size: 'small',
                  error: !!errors.wearDate,
                  fullWidth: true,
                  variant: 'outlined',
                },
              }}
            />
          </LocalizationProvider>
          {errors.wearDate && wearDateError
            ? <span className="err-msg">{translateWearDateError(wearDateError)}</span>
            : null}
          {!errors.wearDate && wearDateBufferDays > 0
            ? <div className="tf-hint">{t('wearDateBufferHint', { count: wearDateBufferDays })}</div>
            : null}
        </div>
        <div className={`mui-tf${errors.poNumber ? ' error' : ''}`}>
          <span className="lbl">
            {t('poNumber')}
            {' '}
            <span className="req">*</span>
          </span>
          <input
            type="text"
            name="poNumber"
            value={fv.poNumber}
            placeholder={t('poPlaceholder')}
            autoComplete="off"
            onChange={(e) => handleChange(null, { name: 'poNumber', value: e.target.value })}
          />
          {errors.poNumber && errorMessages.poNumber
            ? <span className="err-msg">{errorMessages.poNumber as ReactNode}</span>
            : null}
        </div>
      </div>
    );
  };

  const renderCustomizations = () => {
    if (isInventory || fv.fromInventory || !hasCustomizations) {
      return null;
    }

    const summary = selectedCustCount > 0
      ? t('customizations.summarySelected', {
        count: selectedCustCount,
        price: formatCurrency(selectedCustUpcharge),
      })
      : t('customizations.none');

    return (
      <div className="pd-section">
        <div className="rail-label">
          <span className="t">{t('customizations.title')}</span>
          <span className="v">{summary}</span>
        </div>
        <div className="cust-list">
          {customizationsList.map((cust) => {
            // we use the original customizations index so the postData wire
            // format keeps matching the existing server contract.
            const originalIdx = (product.customizations || []).indexOf(cust);
            const checked = !!fv.selectedCustomizations[originalIdx];
            const price = Number(cust.price || 0);
            return (
              <div
                key={originalIdx}
                className={`cust-opt${fv.fromInventory ? ' disabled' : ''}`}
              >
                {/* the note input sits outside the label so typing in it does
                    not bubble up and toggle the checkbox */}
                <label className="cust-opt-row">
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={fv.fromInventory}
                    onChange={() => {
                      const next = { ...fv.selectedCustomizations };
                      next[originalIdx] = !next[originalIdx];
                      handleChange(null, {
                        name: 'selectedCustomizations',
                        value: next,
                      });
                    }}
                  />
                  <span className="name">{cust.name}</span>
                  <span className="upcharge">{`+${formatCurrency(price)}`}</span>
                </label>
                {checked ? (
                  <input
                    type="text"
                    className="cust-note"
                    aria-label={t('customizations.noteLabel', { name: cust.name })}
                    placeholder={t('customizations.notePlaceholder')}
                    value={fv.customizationNotes[originalIdx] || ''}
                    disabled={fv.fromInventory}
                    onChange={(e) => handleChange(null, {
                      name: 'customizationNotes',
                      value: { ...fv.customizationNotes, [originalIdx]: e.target.value },
                    })}
                  />
                ) : null}
              </div>
            );
          })}
        </div>
        <div className="tf-hint">
          <Trans
            t={t}
            i18nKey="customizations.hint"
            values={{ email: appConfig.wholesaleEmail }}
            components={{
              mail: <a href={`mailto:${appConfig.wholesaleEmail}`}>{appConfig.wholesaleEmail}</a>,
            }}
          />
        </div>
      </div>
    );
  };

  const renderShipping = () => {
    if (isInventory) {
      return null;
    }

    return (
      <div className="pd-section">
        <div className="rail-label">
          <span className="t">{t('shipping.title')}</span>
          <span className="v">
            {t('shipping.estShip')}
            &nbsp;
            <b style={{ color: 'var(--pd-brand-navy)', fontWeight: 500 }}>
              {estimatedShipText}
            </b>
          </span>
        </div>

        {fv.fromInventory ? (
          <Alert
            severity="info"
            variant="outlined"
            sx={{ mb: 1 }}
            action={(
              <button
                type="button"
                className="link-btn"
                onClick={() => {
                  setTouched({ wearDate: true, size: true, color: true });
                  setValues({
                    ...fv,
                    maxAmounts: 0,
                    fromInventory: false,
                    inStock: false,
                  });
                  updateShippingWindowInWeeks();
                }}
              >
                {t('actions.clear')}
              </button>
            )}
          >
            {fv.inStock
              ? t('shipping.inStock')
              : t('shipping.inProduction', { date: formatDate(fv.availableDate as string | number | Date) })}
          </Alert>
        ) : (
          <div className="ship-list">
            {shippingWindows.map((sw, idx) => {
              if (!sw.name) return null;
              const sel = fv.selectedShippingWindow === idx;
              const price = Number(sw.price || 0);
              return (
                <label
                  key={`${sw.name}-${idx}`}
                  className={`ship-opt${sel ? ' sel' : ''}`}
                >
                  <input
                    type="radio"
                    name="ship"
                    checked={sel}
                    onChange={() => {
                      fv.selectedShippingWindow = idx;
                      handleChange(null, {
                        name: 'selectedShippingWindow',
                        value: idx,
                      });
                      updateShippingWindowInWeeks();
                    }}
                  />
                  <span className="main">
                    <span className="t">{sw.name}</span>
                    <span className="s">
                      {idx === 0
                        ? t('shipping.shipsWithinWindow')
                        : t('shipping.leadTime', { weeks: sw.weeks })}
                    </span>
                  </span>
                  <span className="upch">{`+${formatCurrency(price)}`}</span>
                </label>
              );
            })}
          </div>
        )}

        {appConfig.shippingDelayInWeeks > 0 && !fv.inStock && !fv.fromInventory ? (
          <div className="tf-hint" style={{ color: 'var(--pd-error)' }}>
            {t('shipping.delayNote', { weeks: appConfig.shippingDelayInWeeks })}
          </div>
        ) : null}
      </div>
    );
  };

  const renderCta = () => {
    if (readonly) {
      return null;
    }
    return (
      <>
        {(!fv.color || !fv.size) && (
          <div className="atc-hint">{t('cta.selectVariantHint')}</div>
        )}
        <div className="pd-cta">
          <div className="qty-field">
            <span className="lbl">{t('cta.qty')}</span>
            <input
              type="number"
              name="amounts"
              min="1"
              value={fv.amounts}
              onChange={(event) => {
                const nextValue = Number(event.target.value);
                if (nextValue <= 0) {
                  return;
                }
                if (fv.maxAmounts && nextValue > fv.maxAmounts) {
                  return;
                }
                handleChange(event, { name: 'amounts', value: nextValue });
              }}
            />
          </div>
          <div className="total-col">
            <div className="lbl">{t('cta.total')}</div>
            <div className="val">{formatCurrency(totalPrice)}</div>
          </div>
          <button
            type="button"
            className="btn-atc"
            disabled={!isValid}
            onClick={handleSubmit}
          >
            <ShoppingBagIcon />
            {isInventory ? t('cta.addToInventory') : t('cta.addToCart')}
          </button>
        </div>
      </>
    );
  };

  const collectionsLabel = (product.collections || []).map((c) => c.name).join(' · ');

  return (
    <Box sx={{ width: '100%' }}>
      <section className="pd">
        {/* LEFT: media */}
        <div className="pd-media">
          <div className="pd-main">
            {renderMediaPreview()}
            {product.isNew ? <span className="badge-new">{t('badge.newProduct')}</span> : null}
            {videoList.length && mediaPreview.type !== 'video' ? (
              <button
                type="button"
                className="video-fab"
                title={t('media.playVideo')}
                onClick={() => {
                  const videoIdx = mediaList.findIndex((m) => m.type === 'video');
                  if (videoIdx >= 0) setMediaPreviewIndex(videoIdx);
                }}
              >
                <PlayArrowIcon />
              </button>
            ) : null}
          </div>
          {renderMediaThumbnails()}
        </div>

        {/* RIGHT: purchase rail */}
        <div className="pd-rail">
          <div>
            {collectionsLabel ? (
              <div className="pd-brand-line">{collectionsLabel}</div>
            ) : null}
            <h1 className="pd-title">{product.name}</h1>
            {product.sku ? (
              <div className="pd-style-line">{t('style', { sku: product.sku })}</div>
            ) : null}
          </div>

          <div className="pd-price-block">
            <div>
              <div className="ws-lbl">{t('price.wholesale')}</div>
              <div className="ws">{formatCurrency(product.price as number)}</div>
            </div>
            {fv.color && fv.size ? (
              <div className="variant">
                {t('price.variant')}
                <b>
                  {variantUpcharge >= 0 ? '+' : '−'}
                  {formatCurrency(Math.abs(variantUpcharge))}
                </b>
              </div>
            ) : null}
          </div>

          {product.description ? (
            <p className="pd-desc">{product.description}</p>
          ) : null}

          {renderColorChips()}
          {renderSizeChips()}
          {renderDateAndPo()}
          {renderCustomizations()}
          {renderShipping()}
          {renderCta()}

          {readonly && !addToOrderUserId && (
            <Box className="pd-alert">
              <Alert variant="outlined" severity="warning">
                {t('alert.noAccess')}
              </Alert>
            </Box>
          )}
          {readonly && addToOrderUserId && (
            <Box className="pd-alert">
              <Alert variant="outlined" severity="warning">
                {t('alert.customerNoAccess')}
              </Alert>
            </Box>
          )}
          {!isValid && !!fv.size && !!errors.size && (
            <Box className="pd-alert">
              <Alert variant="outlined" severity="warning">
                {t('alert.inventoryOnly')}
              </Alert>
            </Box>
          )}
        </div>
      </section>

      {/* Full-width production schedule */}
      <InventoryProduct
        readOnly={isInventory}
        timeZone={appConfig.timeZone}
        product={{
          ...product,
          inventoryProduct: groupInventoryItems(product.inventoryProduct, appConfig.timeZone),
        }}
        onSelect={(value) => {
          setTouched({ wearDate: true });
          setValues({
            ...fv,
            color: value.color,
            size: value.size,
            availableDate: value.inventoryItem.availableDate,
            inStock: value.inventoryItem.availabilityStatus === 'inStock',
            maxAmounts: value.maxAmounts,
            amounts: 1,
            selectedCustomizations: {},
            customizationNotes: {},
            fromInventory: true,
          });
          updateShippingWindowInWeeks(value.inventoryItem.availabilityStatus === 'inStock');
        }}
      />

      {renderFullScreenPreview()}
    </Box>
  );
}
